/**
 * Evaluation Service
 * 평가결과 API 서비스 (BFF API 사용)
 * @see docs/API_DATA_MAPPING_SIMPLE.md
 */

import { platformGet, buildPlatformGetUrl, applyPlatformMedicrewWebHeaders } from './platformApiClient';
import { logResponseData, logErrorResponseData } from './apiClient';
import apiClient from './apiClient';
import type {
	GetClassEvaluationParams,
	GetClassEvaluationResponseDto,
} from '../types/api/evaluation';

/**
 * 클래스 평가결과 조회
 * GET /class/lab/:class_id/evaluation (BFF API)
 * 
 * @param classId - Class ID (PostgreSQL)
 * @param params - Query Parameters (Optional)
 * @returns 평가결과 통계 및 학습자별 데이터
 */
export const getClassEvaluation = async (
	classId: number,
	params?: GetClassEvaluationParams
): Promise<GetClassEvaluationResponseDto> => {
	try {
		const queryParams: Record<string, string | number | boolean | undefined> = {};

		if (params?.contentCode) {
			queryParams.contentCode = params.contentCode;
		}
		if (params?.mode) {
			queryParams.mode = params.mode;
		}
		if (params?.startDate) {
			queryParams.startDate = params.startDate;
		}
		if (params?.endDate) {
			queryParams.endDate = params.endDate;
		}
		if (params?.keyword) {
			queryParams.keyword = params.keyword;
		}
		if (params?.sortBy) {
			queryParams.sortBy = params.sortBy;
		}
		if (params?.sortOrder !== undefined) {
			queryParams.sortOrder = params.sortOrder;
		}
		if (params?.pageNum) {
			queryParams.pageNum = params.pageNum;
		}
		if (params?.countPerPage) {
			queryParams.countPerPage = params.countPerPage;
		}

		const response = await platformGet<GetClassEvaluationResponseDto>(
			`class/lab/${classId}/evaluation`,
			{
				classId,
				params: queryParams,
			}
		);

		logResponseData(response);
		return response;
	} catch (error) {
		logErrorResponseData(error);
		throw error;
	}
};

/**
 * 클래스 평가결과 엑셀 다운로드
 * GET /class/lab/:class_id/evaluation/excel (BFF API)
 * 
 * @param classId - Class ID (PostgreSQL)
 * @param params - Query Parameters (Optional)
 * @param filename - 다운로드할 파일명 (기본값: '평가결과.xlsx')
 * @param language - 언어 코드 (예: 'ko', 'en')
 */
export const downloadClassEvaluationExcel = async (
	classId: number,
	params?: GetClassEvaluationParams,
	filename: string = '평가결과.xlsx',
	language?: string
): Promise<void> => {
	try {
		const queryParams: Record<string, string | number | boolean | undefined> = {};

		if (params?.contentCode) {
			queryParams.contentCode = params.contentCode;
		}
		if (params?.mode) {
			queryParams.mode = params.mode;
		}
		if (params?.startDate) {
			queryParams.startDate = params.startDate;
		}
		if (params?.endDate) {
			queryParams.endDate = params.endDate;
		}
		if (language) {
			queryParams.language = language;
		}

		// URL 생성
		const url = buildPlatformGetUrl(`class/lab/${classId}/evaluation/excel`, { params: queryParams });

		await downloadExcelFile(url, filename);
	} catch (error) {
		console.error('❌ [Evaluation] 엑셀 다운로드 실패:', error);
		logErrorResponseData(error);
		throw error;
	}
};

/**
 * 클래스 전체 평가결과(전체 시나리오) 엑셀 다운로드
 * GET /class/lab/:class_id/evaluation/records/excel (BFF API)
 *
 * @param classId - Class ID (PostgreSQL)
 * @param filename - 다운로드할 파일명 (기본값: '평가결과_전체.xlsx')
 * @param language - 언어 코드 (예: 'ko', 'en')
 */
export const downloadClassEvaluationRecordsExcel = async (
	classId: number,
	filename: string = '평가결과_전체.xlsx',
	language?: string
): Promise<void> => {
	try {
		const queryParams: Record<string, string | number | boolean | undefined> = {};

		if (language) {
			queryParams.language = language;
		}

		// URL 생성
		const url = buildPlatformGetUrl(`class/lab/${classId}/evaluation/records/excel`, { params: queryParams });

		await downloadExcelFile(url, filename);
	} catch (error) {
		console.error('❌ [Evaluation] 전체 평가결과 엑셀 다운로드 실패:', error);
		logErrorResponseData(error);
		throw error;
	}
};

/**
 * BFF 엑셀 URL을 GET으로 호출해 파일로 저장
 * (medicrew web 공통 헤더 + Authorization — Bearer 접두사 없이 토큰 그대로)
 */
const downloadExcelFile = async (url: string, filename: string): Promise<void> => {
	// 헤더 생성
	const headers: Record<string, string> = {};
	applyPlatformMedicrewWebHeaders(headers);

	// Authorization 헤더 추가
	const token = apiClient.getAccessToken();
	if (token) {
		const needsEncoding = !token.includes('%');
		headers.Authorization = needsEncoding ? encodeURIComponent(token) : token;
	}

	// 파일 다운로드
	const response = await fetch(url, {
		method: 'GET',
		headers,
	});

	if (!response.ok) {
		throw new Error(`엑셀 다운로드 실패: ${response.status} ${response.statusText}`);
	}

	// Blob으로 변환
	const blob = await response.blob();

	// 다운로드 링크 생성 및 클릭
	const downloadUrl = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = downloadUrl;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(downloadUrl);

	console.log('✅ [Evaluation] 엑셀 다운로드 성공:', filename);
};
